import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import jwt from 'jwt-decode'
import api from '../../api/api'

export const admin_login = createAsyncThunk(
    'auth/admin_login',
    async (info, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post('/admin-login', info, { withCredentials: true })
            localStorage.setItem('isLoggedIn', 'true')
            localStorage.setItem('userRole', 'admin')
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const seller_login = createAsyncThunk(
    'auth/seller_login',
    async (info, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post('/seller-login', info, { withCredentials: true })

            // If login succeeded, verify the session immediately.
            // This prevents cases where some browsers/devices don't persist/send cookies
            // (or have multiple cookie variants) which would otherwise cause an instant
            // redirect back to /login.
            if (data?.token && !data?.need2FA) {
                try {
                    const headers = {};
                    // Safari (and some Safari-like browsers) may rely on Authorization fallback
                    if (data.isSafari && data.cookieFallback) {
                        headers.Authorization = `Bearer ${data.token}`;
                    }
                    await api.get('/get-user', { withCredentials: true, headers });

                    localStorage.setItem('isLoggedIn', 'true')
                    localStorage.setItem('userRole', 'seller')

                    if (data.isSafari && data.cookieFallback) {
                        localStorage.setItem('authToken', data.token);
                        localStorage.setItem('isSafariAuth', 'true');
                    }
                } catch (sessionError) {
                    localStorage.removeItem('isLoggedIn');
                    localStorage.removeItem('userRole');
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('isSafariAuth');
                    return rejectWithValue({ error: 'Login succeeded but session could not be verified. Your browser may be blocking cookies or using an old session cookie. Please retry or adjust privacy settings.' })
                }
            }
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const logout = createAsyncThunk(
    'auth/logout',
    async ({ navigate, role }, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.get('/logout', { withCredentials: true })

            localStorage.removeItem('isLoggedIn')
            localStorage.removeItem('userRole')
            localStorage.removeItem('isImpersonating')
            localStorage.removeItem('authToken')
            localStorage.removeItem('isSafariAuth')

            if (data.isSafari && data.clearLocalStorage) {
                localStorage.clear()
            }

            if (role === 'admin') {
                navigate('/admin/login')
            } else {
                navigate('/login')
            }

            return fulfillWithValue(data)
        } catch (error) {
            localStorage.removeItem('isLoggedIn')
            localStorage.removeItem('userRole')
            localStorage.removeItem('authToken')
            localStorage.removeItem('isSafariAuth')
            localStorage.removeItem('isImpersonating')

            return rejectWithValue(error.response.data)
        }
    }
)

export const seller_register = createAsyncThunk(
    'auth/seller_register',
    async (info, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post('/seller-register', info, { withCredentials: true })
            localStorage.setItem('isLoggedIn', 'true')
            localStorage.setItem('userRole', 'seller')
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const profile_image_upload = createAsyncThunk(
    'auth/profile_image_upload',
    async (image, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post('/profile-image-upload', image, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const profile_banner_upload = createAsyncThunk(
    'auth/profile_banner_upload',
    async (images, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post('/profile-banner-upload', images, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const profile_info_add = createAsyncThunk(
    'auth/profile_info_add',
    async (info, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post('/profile-info-add', info, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const get_user_info = createAsyncThunk(
    'auth/get_user_info',
    async (_, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.get('/get-user', { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

const returnRole = (token) => {
    if (token) {
        try {
            const userInfo = jwt(token);
            return userInfo.role;
        } catch (error) {
            localStorage.removeItem('isLoggedIn');
            localStorage.removeItem('userRole');
            return '';
        }
    } else {
        return localStorage.getItem('userRole') || '';
    }
};

export const verify_2fa = createAsyncThunk(
    'auth/verify_2fa',
    async (info, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post('/verify-2fa', info, { withCredentials: true });

            if (data?.token) {
                try {
                    const headers = {};
                    if (data.isSafari && data.cookieFallback) {
                        headers.Authorization = `Bearer ${data.token}`;
                    }
                    await api.get('/get-user', { withCredentials: true, headers });

                    localStorage.setItem('isLoggedIn', 'true');
                    localStorage.setItem('userRole', 'seller');

                    if (data.isSafari && data.cookieFallback) {
                        localStorage.setItem('authToken', data.token);
                        localStorage.setItem('isSafariAuth', 'true');
                    }
                } catch (sessionError) {
                    localStorage.removeItem('isLoggedIn');
                    localStorage.removeItem('userRole');
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('isSafariAuth');
                    return rejectWithValue({ error: '2FA succeeded but session could not be verified. Your browser may be blocking cookies. Please retry.' })
                }
            }
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

export const refresh_token = createAsyncThunk(
    'auth/refresh_token',
    async (_, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post('/refresh-token', {}, { withCredentials: true });
            return fulfillWithValue(data);
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

// Impersonation actions
export const start_impersonation = createAsyncThunk(
    'auth/start_impersonation',
    async (sellerId, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post('/admin/impersonate-seller', { sellerId }, { withCredentials: true })

            localStorage.setItem('userRole', 'seller')
            localStorage.setItem('isImpersonating', 'true')

            if (data.isSafari && data.cookieFallback) {
                localStorage.setItem('authToken', data.token);
                localStorage.setItem('isSafariAuth', 'true');
            }

            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const stop_impersonation = createAsyncThunk(
    'auth/stop_impersonation',
    async (_, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post('/admin/stop-impersonation', {}, { withCredentials: true })

            // Set the correct user role based on the response
            const userRole = data.userInfo.accountType === 'staff' ? data.userInfo.role : 'admin'
            localStorage.setItem('userRole', userRole)
            localStorage.removeItem('isImpersonating')

            if (data.isSafari && data.cookieFallback) {
                localStorage.setItem('authToken', data.token);
                localStorage.setItem('isSafariAuth', 'true');
            }

            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const get_impersonation_status = createAsyncThunk(
    'auth/get_impersonation_status',
    async (_, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.get('/admin/impersonation-status', { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const delete_banner = createAsyncThunk(
    'auth/delete_banner',
    async (bannerUrl, { rejectWithValue, fulfillWithValue }) => {
        try {
            const { data } = await api.post('/delete-banner', { bannerUrl }, { withCredentials: true })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const authReducer = createSlice({
    name: 'auth',
    initialState: {
        successMessage: '',
        errorMessage: '',
        loader: false,
        authLoading: true,
        userInfo: '',
        role: localStorage.getItem('userRole') || '',
        token: null,
        isLoggedIn: localStorage.getItem('isLoggedIn') === 'true',
        need2FA: false,
        tempEmail: '',
        isImpersonating: localStorage.getItem('isImpersonating') === 'true',
        impersonationData: null
    },
    reducers: {
        messageClear: (state, _) => {
            state.errorMessage = ""
            state.successMessage = ""
        },
        reset2FAState: (state, _) => {
            state.need2FA = false;
            state.tempEmail = '';
        },
        setAuthLoading: (state, { payload }) => {
            state.authLoading = payload;
        },
        updateUserInfo: (state, { payload }) => {
            state.userInfo = { ...state.userInfo, ...payload };
        }
    },
    extraReducers: {
        [admin_login.pending]: (state, _) => {
            state.loader = true
        },
        [admin_login.rejected]: (state, { payload }) => {
            state.loader = false
            state.errorMessage = payload.error
        },
        [admin_login.fulfilled]: (state, { payload }) => {
            state.loader = false
            state.successMessage = payload.message
            state.token = payload.token
            state.role = returnRole(payload.token)
            state.isLoggedIn = true

            // Handle staff login
            if (payload.userInfo && payload.userInfo.accountType === 'staff') {
                state.userInfo = {
                    ...payload.userInfo,
                    accountType: payload.userInfo.accountType,
                    staffRole: payload.userInfo.staffRole,
                    permissions: payload.userInfo.permissions
                }
            } else {
                // Regular admin login
                state.userInfo = payload.userInfo || ''
            }
        },
        [seller_login.pending]: (state, _) => {
            state.loader = true
        },
        [seller_login.rejected]: (state, { payload }) => {
            state.loader = false
            state.errorMessage = payload.error
        },
        [seller_login.fulfilled]: (state, { payload }) => {
            state.loader = false;

            if (payload.need2FA) {
                state.need2FA = true;
                state.tempEmail = payload.email;
                state.successMessage = payload.message;
                return;
            }

            state.successMessage = payload.message;
            state.token = payload.token;
            state.role = returnRole(payload.token);
            state.isLoggedIn = true;

            state.userInfo = {
                ...payload.userInfo,
                accountType: payload.userInfo.accountType,
                representative: payload.userInfo.representative,
            };

            state.need2FA = false;
            state.tempEmail = "";
        },
        [verify_2fa.pending]: (state, _) => {
            state.loader = true
        },
        [verify_2fa.rejected]: (state, { payload }) => {
            state.loader = false
            state.errorMessage = payload.error
        },
        [verify_2fa.fulfilled]: (state, { payload }) => {
            state.loader = false;
            state.successMessage = payload.message;
            state.token = payload.token;
            state.role = returnRole(payload.token);
            state.need2FA = false;
            state.tempEmail = "";
            state.isLoggedIn = true;

            state.userInfo = {
                ...payload.userInfo,
                accountType: payload.userInfo.accountType,
                representative: payload.userInfo.representative,
            };
        },
        [seller_register.pending]: (state, _) => {
            state.loader = true
        },
        [seller_register.rejected]: (state, { payload }) => {
            state.loader = false
            state.errorMessage = payload.error
        },
        [seller_register.fulfilled]: (state, { payload }) => {
            state.loader = false;
            state.successMessage = payload.message;
            state.token = payload.token;
            state.role = returnRole(payload.token);
            state.isLoggedIn = true;

            state.userInfo = {
                ...payload.userInfo,
                accountType: payload.userInfo.accountType,
            };
        },
        [get_user_info.fulfilled]: (state, { payload }) => {
            state.loader = false;
            state.authLoading = false;

            state.userInfo = {
                ...payload.userInfo,
                accountType: payload.userInfo.accountType,
                representative: payload.userInfo.representative,
            };

            state.role = payload.userInfo.role;

            // Handle impersonation state if present
            if (payload.userInfo.isImpersonating) {
                state.isImpersonating = true;
                state.impersonationData = {
                    impersonatedSeller: payload.userInfo,
                    impersonatedBy: payload.userInfo.impersonatedBy,
                    impersonatedAt: payload.userInfo.impersonatedAt
                };
            } else {
                state.isImpersonating = false;
            }
        },
        [get_user_info.rejected]: (state, { payload }) => {
            state.loader = false;
            state.authLoading = false;
        },
        [profile_image_upload.pending]: (state, _) => {
            state.loader = true
        },
        [profile_image_upload.fulfilled]: (state, { payload }) => {
            state.loader = false
            state.userInfo = payload.userInfo
            state.successMessage = payload.message
        },
        [profile_banner_upload.pending]: (state, _) => {
            state.loader = true
        },
        [profile_banner_upload.fulfilled]: (state, { payload }) => {
            state.loader = false
            state.userInfo = payload.userInfo
            state.successMessage = payload.message
        },
        [profile_info_add.pending]: (state, _) => {
            state.loader = true
        },
        [profile_info_add.fulfilled]: (state, { payload }) => {
            state.loader = false
            state.userInfo = payload.userInfo
            state.successMessage = payload.message
        },
        [logout.pending]: (state, _) => {
            state.loader = true
        },
        [logout.rejected]: (state, { payload }) => {
            state.loader = false
            state.errorMessage = payload?.error || 'Logout failed'
        },
        [logout.fulfilled]: (state, { payload }) => {
            state.loader = false
            state.successMessage = payload.message
            state.token = null
            state.role = ''
            state.userInfo = ''
            state.isLoggedIn = false
            state.need2FA = false
            state.tempEmail = ''
            state.isImpersonating = false
            state.impersonationData = null
        },
        [refresh_token.fulfilled]: (state, { payload }) => {
            if (payload.token) {
                state.token = payload.token;
            }
        },
        [start_impersonation.pending]: (state, _) => {
            state.loader = true
        },
        [start_impersonation.rejected]: (state, { payload }) => {
            state.loader = false
            state.errorMessage = payload.error
        },
        [start_impersonation.fulfilled]: (state, { payload }) => {
            state.loader = false
            state.successMessage = payload.message
            state.isImpersonating = true
            state.role = 'seller'

            state.userInfo = {
                ...payload.userInfo,
                accountType: payload.userInfo.accountType,
                representative: payload.userInfo.representative,
            };

            state.impersonationData = {
                impersonatedSeller: payload.userInfo,
                impersonatedBy: payload.userInfo.impersonatedBy
            };
        },
        [stop_impersonation.pending]: (state, _) => {
            state.loader = true
        },
        [stop_impersonation.rejected]: (state, { payload }) => {
            state.loader = false
            state.errorMessage = payload.error
        },
        [stop_impersonation.fulfilled]: (state, { payload }) => {
            state.loader = false
            state.successMessage = payload.message
            state.isImpersonating = false
            // Set the correct role based on the user's account type
            state.role = payload.userInfo.accountType === 'staff' ? payload.userInfo.role : 'admin'
            state.impersonationData = null

            state.userInfo = {
                ...payload.userInfo,
                accountType: payload.userInfo.accountType,
                representative: payload.userInfo.representative,
            };

            // Add staff permissions if user is staff
            if (payload.userInfo.accountType === 'staff') {
                state.userInfo.permissions = payload.userInfo.permissions;
                state.userInfo.staffRole = payload.userInfo.role;
            }
        },
        [get_impersonation_status.fulfilled]: (state, { payload }) => {
            if (payload.isImpersonating) {
                state.isImpersonating = true;
                state.impersonationData = {
                    impersonatedSeller: payload.impersonatedSeller,
                    impersonatingAdmin: payload.impersonatingAdmin,
                    impersonatedAt: payload.impersonatedAt
                };
            } else {
                state.isImpersonating = false;
                state.impersonationData = null;
            }
        },
        [get_impersonation_status.rejected]: (state, { payload }) => {
            // If impersonation status check fails, clear impersonation state
            state.isImpersonating = false;
            state.impersonationData = null;
            console.log('Failed to get impersonation status:', payload);
        },
        [delete_banner.pending]: (state, _) => {
            state.loader = true
        },
        [delete_banner.fulfilled]: (state, { payload }) => {
            state.loader = false
            state.userInfo = payload.userInfo
            state.successMessage = payload.message
        },
    }
})

export const { messageClear, reset2FAState, setAuthLoading, updateUserInfo } = authReducer.actions
export default authReducer.reducer
